import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { db, functions } from '../lib/firebase';
import type { WorkspaceSetupState } from '../../functions/src/agentAdvisor/types';

interface UseWorkspaceSetupStateResult {
  state: WorkspaceSetupState | null;
  loading: boolean;
  error: string | null;
  fetch: (workspaceId: string) => Promise<WorkspaceSetupState | null>;
}

function withDay0Defaults(state: WorkspaceSetupState): WorkspaceSetupState {
  return {
    ...state,
    widget: {
      configured: state.widget?.configured ?? false,
      hasToken: state.widget?.hasToken ?? false,
      appearanceSaved: state.widget?.appearanceSaved ?? false,
    },
    onboarding: {
      publicUrlConfirmed: state.onboarding?.publicUrlConfirmed ?? false,
      contactPathDecided: state.onboarding?.contactPathDecided ?? false,
    },
  };
}

async function enrichDay0State(
  workspaceId: string,
  state: WorkspaceSetupState
): Promise<WorkspaceSetupState> {
  const next = withDay0Defaults(state);
  try {
    const [onboardingSnap, widgetSnap, tokensSnap] = await Promise.all([
      getDoc(doc(db, `publicPages/${workspaceId}/settings`, 'onboarding')),
      getDoc(doc(db, `publicPages/${workspaceId}/settings`, 'widget')),
      getDocs(
        query(collection(db, 'publicApiTokens'), where('workspaceId', '==', workspaceId), limit(20))
      ),
    ]);
    const embedReady = tokensSnap.docs.some((tokenDoc) => {
      const origins = tokenDoc.get('allowedOrigins');
      return tokenDoc.get('isActive') === true && Array.isArray(origins) && origins.length > 0;
    });
    return {
      ...next,
      widget: {
        ...next.widget,
        hasToken: embedReady,
        appearanceSaved:
          widgetSnap.get('appearanceSaved') === true ||
          (widgetSnap.get('button') != null && typeof widgetSnap.get('button') === 'object'),
      },
      onboarding: {
        publicUrlConfirmed: onboardingSnap.get('publicUrlConfirmed') === true,
        contactPathDecided: onboardingSnap.get('contactPathDecided') === true,
      },
    };
  } catch (err) {
    console.warn('[useWorkspaceSetupState] Day 0 enrich failed, using callable state', err);
    return next;
  }
}

export function useWorkspaceSetupState(): UseWorkspaceSetupStateResult {
  const [state, setState] = useState<WorkspaceSetupState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async (workspaceId: string) => {
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable<{ workspaceId: string }, { state: WorkspaceSetupState }>(
        functions,
        'getWorkspaceSetupStateFn'
      );
      const res = await fn({ workspaceId });
      const next = await enrichDay0State(workspaceId, res.data.state);
      setState(next);
      return next;
    } catch (err: any) {
      console.error('[useWorkspaceSetupState] fetch error', err);
      setError(err?.message ?? 'Failed to fetch workspace setup state');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { state, loading, error, fetch };
}
